import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import artifactContentCopy from '@/i18n/ns/artifact-content';
import type { WorkbookCell, WorkbookPayload } from '../models.ts';
import { formatScalar } from '../format.ts';
import { projectNullDisplay } from '../../../../src/artifacts/presentation/null-display.mjs';
import { presentationStyle } from '../presentation/style.ts';
import { numericMetadataForCell } from '../../../../src/artifacts/models/presentation.mjs';
import { legacyNumberFormat } from '../../../../src/artifacts/inspection/legacy-number-format.mjs';
import {
  addressing,
  columnLetters,
  formulaText,
  referencedCells,
  type Addressing,
} from './formula-text.ts';
import './sheet.css';

// A read-oriented sheet: tabs, a grid, a formula bar. It shows the values and the
// formulas the artifact stores and edits nothing; the only edits are the
// inspector's, through canonical presentation slots. A formula cell shows its
// cached value, and a cache in error shows the error, never zero.

const ROW_HEIGHT = 32;
// A phone or coarse pointer gets 44px rows: a tap target is a product requirement.
const COARSE_ROW_HEIGHT = 44;
const COARSE = '(max-width: 760px), (pointer: coarse)';
const OVERSCAN = 6;

type Sheet = WorkbookPayload['sheets'][number];
type Props = {
  payload: WorkbookPayload;
  locale?: string;
  className?: string;
  style?: CSSProperties;
  /** Rows drawn before the first scroll measurement; the scroller measures after mount. */
  initialRows?: number;
};

const ERROR_KEYS = {
  cycle: 'errorCycle',
  unsupported: 'errorUnsupported',
  'missing-input': 'errorMissingInput',
  'division-by-zero': 'errorDivisionByZero',
  'type-error': 'errorTypeError',
} as const;

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export function SheetView({ payload, locale = 'en', className, style, initialRows = 40 }: Props) {
  const copy = useNamespaceCopy(artifactContentCopy);
  const [sheetId, setSheetId] = useState(payload.sheets[0]?.id ?? '');
  // The one roving grid position, by zero-based row and column: it exists for every
  // coordinate (empty cells too), and the formula bar follows it.
  const [at, setAt] = useState<{ row: number; col: number } | null>(null);
  const [top, setTop] = useState(0);
  const [viewportRows, setViewportRows] = useState(0);
  const [rowHeight, setRowHeight] = useState(ROW_HEIGHT);
  const scroller = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLTableElement>(null);
  useEffect(() => {
    const query = window.matchMedia?.(COARSE);
    if (!query) return;
    const apply = () => setRowHeight(query.matches ? COARSE_ROW_HEIGHT : ROW_HEIGHT);
    apply();
    query.addEventListener?.('change', apply);
    return () => query.removeEventListener?.('change', apply);
  }, []);
  const places = useMemo(() => addressing(payload), [payload]);
  // `sheetId` can outlive its sheet: a revision may drop it while the tab is open.
  const sheet = payload.sheets.find((s) => s.id === sheetId) ?? payload.sheets[0];
  const cells = useMemo(() => {
    const map = new Map<string, WorkbookCell>();
    for (const cell of sheet?.cells ?? []) map.set(`${cell.rowId}\u0000${cell.columnId}`, cell);
    return map;
  }, [sheet]);
  const rowCount = sheet?.rowIds.length ?? 0;
  const colCount = sheet?.columnIds.length ?? 0;

  // How many rows the scroller actually shows, measured; `initialRows` only until then.
  useLayoutEffect(() => {
    const node = scroller.current;
    if (!node) return;
    const measure = () => {
      const rows = node.clientHeight > 0 ? Math.ceil(node.clientHeight / rowHeight) : 0;
      setViewportRows((was) => (was === rows ? was : rows));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [rowHeight, sheet?.id]);
  const visible = viewportRows > 0 ? viewportRows : initialRows;

  // A different sheet starts at its top with nothing selected; the same sheet in a new
  // revision keeps its place but never past its end (a shorter sheet must not leave an
  // empty window behind a spacer the browser cannot clamp away).
  const shownSheet = sheet?.id;
  useLayoutEffect(() => {
    setAt(null);
    setTop(0);
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [shownSheet]);
  useLayoutEffect(() => {
    const limit = Math.max(0, rowCount * rowHeight - visible * rowHeight);
    setTop((was) => (was > limit ? limit : was));
    if (scroller.current && scroller.current.scrollTop > limit) scroller.current.scrollTop = limit;
    setAt((was) =>
      was && (rowCount === 0 || colCount === 0)
        ? null
        : was && (was.row >= rowCount || was.col >= colCount)
          ? { row: Math.min(was.row, rowCount - 1), col: Math.min(was.col, colCount - 1) }
          : was,
    );
  }, [rowCount, colCount, rowHeight, visible]);
  if (!sheet) return null;

  const firstRow = clamp(Math.floor(top / rowHeight) - OVERSCAN, 0, Math.max(0, rowCount - 1));
  const lastRow = Math.min(rowCount, firstRow + visible + OVERSCAN * 2);
  const cellAt = (row: number, col: number) =>
    cells.get(`${sheet.rowIds[row]}\u0000${sheet.columnIds[col]}`);
  const cell = at ? cellAt(at.row, at.col) : undefined;
  // The cells a selected formula reads, in the selection colours (a plain set, no hook:
  // this runs after the empty-sheet return).
  const reads = cell?.kind === 'formula' ? referencedCells(cell.ast, sheet.id) : new Set<string>();
  const cellDomId = (row: number, col: number) => `ws-cell-${sheet.id}-${row}-${col}`;
  const text = (
    c: WorkbookCell,
  ): { text: string; state?: string; align?: 'end'; glyph?: string } => {
    if (c.kind === 'error') return { text: copy.errorGeneric, state: 'error' };
    if (c.kind === 'formula') {
      if (c.cache.status === 'error')
        return { text: copy[ERROR_KEYS[c.cache.code]], state: 'error' };
      return display(c.cache.value, formatOf(c));
    }
    return display(c.slot.kind === 'literal' ? c.slot.value : c.slot.observed, formatOf(c));
  };
  // The typed override wins; a retained legacy spelling is read through INS's admitted
  // map (exactly four spellings); anything else is the scalar's own native format.
  const formatOf = (c: WorkbookCell) =>
    c.presentation?.numberFormat ??
    (typeof c.numberFormat === 'string'
      ? legacyNumberFormat(c.numberFormat, numericMetadataForCell(c), locale)
      : null);
  const display = (
    value: Parameters<typeof formatScalar>[0],
    format?: ReturnType<typeof formatOf>,
  ) => {
    const shown = formatScalar(value, locale, format);
    // Numbers read from the right, in tabular figures; text and dates from the start.
    if (shown.kind === 'text')
      return { text: shown.text, align: value.kind === 'decimal' ? ('end' as const) : undefined };
    const label =
      shown.reason === 'redacted'
        ? copy.valueRedacted
        : shown.reason === 'source-null'
          ? copy.valueSourceNull
          : copy.valueMissing;
    const glyph =
      format && value.kind === 'null'
        ? projectNullDisplay(value, { locale, surface: 'table', nullStyle: format.nullStyle }).text
        : '—';
    return { text: label, state: 'null', glyph };
  };

  // Bring a row into the scroller (the sticky header takes one row of height).
  const reveal = (row: number) => {
    const node = scroller.current;
    const height = node && node.clientHeight > 0 ? node.clientHeight : visible * rowHeight;
    const rowTop = row * rowHeight;
    const current = node?.scrollTop ?? top;
    let next = current;
    if (rowTop < current) next = rowTop;
    else if (rowTop + 2 * rowHeight > current + height) next = rowTop + 2 * rowHeight - height;
    next = clamp(next, 0, Math.max(0, rowCount * rowHeight - height));
    if (next !== current) {
      if (node) node.scrollTop = next;
      setTop(next);
    }
  };
  const move = (row: number, col: number) => {
    const next = { row: clamp(row, 0, rowCount - 1), col: clamp(col, 0, colCount - 1) };
    setAt(next);
    reveal(next.row);
  };
  const onGridKey = (event: KeyboardEvent) => {
    if (rowCount === 0 || colCount === 0) return;
    const cur = at ?? { row: 0, col: 0 };
    const page = Math.max(1, visible - 2);
    const jump = event.ctrlKey || event.metaKey;
    switch (event.key) {
      case 'ArrowDown':
        return act(event, () => move(cur.row + 1, cur.col));
      case 'ArrowUp':
        return act(event, () => move(cur.row - 1, cur.col));
      case 'ArrowRight':
        return act(event, () => move(cur.row, cur.col + 1));
      case 'ArrowLeft':
        return act(event, () => move(cur.row, cur.col - 1));
      case 'PageDown':
        return act(event, () => move(cur.row + page, cur.col));
      case 'PageUp':
        return act(event, () => move(cur.row - page, cur.col));
      case 'Home':
        return act(event, () => move(jump ? 0 : cur.row, 0));
      case 'End':
        return act(event, () => move(jump ? rowCount - 1 : cur.row, colCount - 1));
    }
  };
  const act = (event: KeyboardEvent, run: () => void) => {
    event.preventDefault();
    run();
  };

  const onTabKeys = (event: KeyboardEvent, index: number) => {
    const to =
      event.key === 'ArrowRight'
        ? index + 1
        : event.key === 'ArrowLeft'
          ? index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? payload.sheets.length - 1
              : null;
    if (to === null) return;
    event.preventDefault();
    const next = payload.sheets[(to + payload.sheets.length) % payload.sheets.length];
    setSheetId(next.id);
    document.getElementById(`ws-sheet-tab-${next.id}`)?.focus();
  };

  return (
    <div
      className={className ? `ws-sheet ${className}` : 'ws-sheet'}
      style={style}
      data-kind="spreadsheet"
    >
      <FormulaBar
        cell={cell}
        sheet={sheet}
        places={places}
        address={at ? `${columnLetters(at.col)}${at.row + 1}` : null}
        text={cell ? text(cell).text : ''}
      />
      <div
        className="ws-sheet-scroll"
        ref={scroller}
        onScroll={(event) => setTop(event.currentTarget.scrollTop)}
      >
        <table
          className="ws-sheet-grid"
          ref={grid}
          role="grid"
          aria-label={formatCopy(copy.sheetGrid, { title: sheet.title })}
          aria-rowcount={rowCount + 1}
          aria-colcount={colCount + 1}
          aria-activedescendant={at ? cellDomId(at.row, at.col) : undefined}
          tabIndex={0}
          onKeyDown={onGridKey}
          // Entering the grid by keyboard or pointer lands on the first cell when nothing is chosen.
          onFocus={() =>
            setAt((was) => was ?? (rowCount > 0 && colCount > 0 ? { row: 0, col: 0 } : null))
          }
        >
          <thead>
            <tr>
              <th className="corner" scope="col" />
              {sheet.columnIds.map((id, i) => (
                <th key={id} scope="col">
                  {columnLetters(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {firstRow > 0 && (
              <tr aria-hidden="true" style={{ height: firstRow * rowHeight }}>
                <td colSpan={colCount + 1} />
              </tr>
            )}
            {sheet.rowIds.slice(firstRow, lastRow).map((rowId, offset) => {
              const row = firstRow + offset;
              return (
                <tr key={rowId} aria-rowindex={row + 2} style={{ height: rowHeight }}>
                  <th scope="row">{row + 1}</th>
                  {sheet.columnIds.map((columnId, col) => {
                    const c = cells.get(`${rowId}\u0000${columnId}`);
                    const shown = c ? text(c) : null;
                    const selected = at?.row === row && at.col === col;
                    return (
                      <td
                        key={columnId}
                        id={cellDomId(row, col)}
                        role="gridcell"
                        data-inspect-node={c?.id}
                        data-cell-kind={c?.kind}
                        data-state={shown?.state}
                        data-align={shown?.align}
                        data-ref={c && reads.has(c.id) ? 'true' : undefined}
                        style={c ? presentationStyle(c.presentation, 'themed') : undefined}
                        data-row={row}
                        data-col={col}
                        aria-colindex={col + 2}
                        aria-selected={selected}
                        onClick={() => {
                          setAt({ row, col });
                          grid.current?.focus();
                        }}
                      >
                        {shown && <CellText shown={shown} />}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {lastRow < rowCount && (
              <tr aria-hidden="true" style={{ height: (rowCount - lastRow) * rowHeight }}>
                <td colSpan={colCount + 1} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="ws-sheet-tabs" role="tablist" aria-label={copy.sheetTabs}>
        {payload.sheets.map((s, index) => (
          <button
            key={s.id}
            id={`ws-sheet-tab-${s.id}`}
            role="tab"
            aria-selected={s.id === sheet.id}
            tabIndex={s.id === sheet.id ? 0 : -1}
            onClick={() => setSheetId(s.id)}
            onKeyDown={(event) => onTabKeys(event, index)}
          >
            {s.title}
          </button>
        ))}
      </div>
    </div>
  );
}

function FormulaBar({
  cell,
  sheet,
  places,
  address,
  text,
}: {
  cell?: WorkbookCell;
  sheet: Sheet;
  places: Addressing;
  address: string | null;
  text: string;
}) {
  const copy = useNamespaceCopy(artifactContentCopy);
  const sources =
    cell?.kind === 'value' && cell.slot.kind === 'bound' ? cell.slot.evidence.length : 0;
  return (
    <div className="ws-sheet-bar" role="status">
      <span className="address">{address ?? ''}</span>
      <span className="label">{copy.formulaBar}</span>
      <span className="content" data-empty={cell || address ? undefined : 'true'}>
        {!address
          ? copy.formulaBarEmpty
          : !cell
            ? ''
            : cell.kind === 'formula'
              ? formulaText(cell.ast, sheet.id, places)
              : text}
      </span>
      {sources > 0 && (
        <span className="sources">
          {sources === 1 ? copy.fromSource : formatCopy(copy.fromSources, { count: sources })}
        </span>
      )}
    </div>
  );
}

// A missing, hidden or errored value is never bare text in a number column: a dash for
// a null (its reason is the accessible name and the tooltip, as in every viewer), a
// warning glyph for an error so it does not rely on colour.
function CellText({ shown }: { shown: { text: string; state?: string; glyph?: string } }) {
  if (shown.state === 'null')
    return (
      <span role="img" aria-label={shown.text} title={shown.text}>
        <span aria-hidden="true">{shown.glyph ?? '—'}</span>
      </span>
    );
  if (shown.state === 'error')
    return (
      <>
        <span className="glyph" aria-hidden="true">
          ⚠
        </span>
        {shown.text}
      </>
    );
  return <>{shown.text}</>;
}
